import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestHeaders,
  AxiosResponse,
  InternalAxiosRequestConfig
} from 'axios'

import { ElMessage, ElMessageBox, ElNotification } from 'element-plus'
import qs from 'qs'
import { config } from '@/config/axios/config'
import { getAccessToken, getRefreshToken, getTenantId, removeToken, setToken } from '@/utils/auth'
import errorCode from './errorCode'
import { parseFileNameFromContentDisposition } from '@/utils/download'

import { resetRouter } from '@/router'
import { deleteUserCache } from '@/hooks/web/useCache'
import { refreshToken } from '@/api/login/index'

const tenantEnable = import.meta.env.VITE_APP_TENANT_ENABLE
const { result_code, base_url, request_timeout } = config

// 需要忽略的提示。忽略后，自动 Promise.reject('error')
const ignoreMsgs = [
  '无效的刷新令牌', // 刷新令牌被删除时，不用提示
  '刷新令牌已过期' // 使用刷新令牌，刷新获取新的访问令牌时，结果因为过期失败，此时需要忽略。否则，会导致继续 401，无法跳转到登出界面
]

// 90002: 未登录/登录过期，10006: token 失效
const expireCodeMap = {
  90002: '未登录或登录已过期',
  10006: '用户域token 失效'
}

// 是否显示重新登录
export const isRelogin = { show: false }
// Axios 无感知刷新令牌
// 请求队列
let requestList: any[] = []
// 是否正在刷新中
let isRefreshToken = false
// 请求白名单，无须token的接口
const whiteList: string[] = ['/login', '/refresh-token']

// 创建axios实例
const service: AxiosInstance = axios.create({
  baseURL: base_url, // api 的 base_url
  timeout: request_timeout, // 请求超时时间
  withCredentials: false // 禁用 Cookie 等信息
})

// request拦截器
service.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 是否需要设置 token
    let isToken = (config!.headers || {}).isToken === false
    whiteList.some((v) => {
      if (config.url && config.url.indexOf(v) > -1) {
        return (isToken = false)
      }
    })
    if (getAccessToken() && !isToken && !(config as Recordable).skipAuthRefresh) {
      ;(config as Recordable).headers.Authorization = 'Bearer ' + getAccessToken() // 让每个请求携带自定义token
    }
    // 设置租户
    if (tenantEnable && tenantEnable === 'true') {
      const tenantId = getTenantId()
      if (tenantId) (config as Recordable).headers['tenant-id'] = tenantId
    }
    const params = config.params || {}
    const data = config.data || false
    if (
      config.method?.toUpperCase() === 'POST' &&
      (config.headers as AxiosRequestHeaders)['Content-Type'] ===
        'application/x-www-form-urlencoded'
    ) {
      config.data = qs.stringify(data)
    }
    // get参数编码
    if (config.method?.toUpperCase() === 'GET' && params) {
      config.params = {}
      const paramsStr = qs.stringify(params, { allowDots: true })
      if (paramsStr) {
        config.url = config.url + '?' + paramsStr
      }
    }
    return config
  },
  (error: AxiosError) => {
    // Do something with request error
    console.log(error) // for debug
    Promise.reject(error)
  }
)

// response 拦截器
service.interceptors.response.use(
  async (response: AxiosResponse<any>) => {
    let { data } = response
    const config = response.config
    if (!data) {
      // 返回“[HTTP]请求没有返回值”;
      throw new Error()
    }
    // 未设置状态码则默认成功状态
    // 二进制数据则直接返回，例如说 Excel 导出
    if (
      response.request.responseType === 'blob' ||
      response.request.responseType === 'arraybuffer'
    ) {
      // 注意：如果导出的响应为 json，说明可能失败了，不直接返回进行下载
      if (response.data.type !== 'application/json') {
        // 后端在 Content-Disposition 里给出下载文件名，挂到 Blob 上供下载方读取。
        // 不可枚举，避免影响既有的 blob 消费方（identity / channel 等十余处）
        const fileName = parseFileNameFromContentDisposition(
          response.headers?.['content-disposition'] as string | undefined
        )
        if (fileName) {
          Object.defineProperty(response.data, 'fileName', { value: fileName, enumerable: false })
        }
        return response.data
      }
      data = await new Response(response.data).json()
    }
    const code = data.code ?? result_code
    // 获取错误信息
    const msg = data.message || errorCode[code] || errorCode['default']
    if (ignoreMsgs.indexOf(msg) !== -1) {
      // 如果是忽略的错误码，直接返回 msg 异常
      return Promise.reject(msg)
    } else if (expireCodeMap[code]) {
      // 未登录/登录过期(90002) 或 token 失效(10006)：走刷新令牌流程
      // 刷新请求自身的失败（skipAuthRefresh 标记）：刷新令牌已失效，直接抛错让上层 catch 后登出，避免递归/死锁
      if ((config as Recordable).skipAuthRefresh) {
        return Promise.reject(new Error('refresh-token-expired'))
      }
      if (!isRefreshToken) {
        isRefreshToken = true
        // 1. 如果获取不到刷新令牌，则只能执行登出操作
        if (!getRefreshToken()) {
          return handleAuthorized()
        }
        // 2. 进行刷新访问令牌（成功后返回解包后的 token 对象）
        try {
          const tokenData = await refreshToken()
          // 刷新失败（刷新令牌无效/过期）：后端仍返回 HTTP 200，只是缺少 accessToken/refreshToken，
          // 必须显式识别并走登出兜底，否则会拿着坏 token 反复重试，请求一直转圈。
          if (!tokenData?.accessToken || !tokenData?.refreshToken) {
            throw new Error('refresh-token-expired')
          }
          // 2.1 刷新成功，则回放队列的请求 + 当前请求
          setToken(tokenData)
          config.headers!.Authorization = 'Bearer ' + getAccessToken()
          requestList.forEach((cb: any) => {
            cb()
          })
          requestList = []
          return service(config)
        } catch (e) {
          // 2.2 刷新失败：清空队列，不再回放（回放只会再次 90002/10006），统一登出
          const queued = requestList.splice(0)
          queued.forEach((cb: any) => {
            cb(e)
          })
          return handleAuthorized()
        } finally {
          requestList = []
          isRefreshToken = false
        }
      } else {
        // 添加到队列，等待刷新获取到新的令牌
        return new Promise((resolve, reject) => {
          requestList.push((err?: unknown) => {
            if (err) {
              reject(err)
              return
            }
            config.headers!.Authorization = 'Bearer ' + getAccessToken() // 让每个请求携带自定义token 请根据实际情况自行修改
            resolve(service(config))
          })
        })
      }
    } else if (code !== 0) {
      // 登录失败次数类错误（如 10001 账号或密码错误）在 data 里带 remainingCount，
      // 把剩余可尝试次数并入提示，登录页内联提示与全局通知都能直接看到。
      const attemptInfo = data.data as
        { failedCount?: number; remainingCount?: number; totalCount?: number } | undefined
      const tip =
        attemptInfo && typeof attemptInfo.remainingCount === 'number'
          ? attemptInfo.remainingCount > 0
            ? `${msg}，还可尝试 ${attemptInfo.remainingCount} 次`
            : `${msg}，失败次数已达上限，请稍后重试`
          : msg
      if (msg === '无效的刷新令牌') {
        // hard coding：忽略这个提示，直接登出
        console.log(msg)
      } else if (code === 40063 || code === 40066) {
        // 招商大会：40063=二次确认、40066=版本冲突，均由 CampaignFormDialog 自行弹窗处理，不走全局报错提示。
        // 当前仅 CampaignFormDialog 使用；新增调用方须自行处理这两个码，否则会静默无反馈
      } else {
        ElNotification.error({ title: tip })
      }
      // 用 Error 承载 code 与 data 供调用方精细化处理；历史调用方只读 message，行为不变
      const err = new Error(tip) as Error & { code?: number; data?: unknown }
      err.code = code
      err.data = data.data
      return Promise.reject(err)
    } else {
      return data
    }
  },
  (error: AxiosError) => {
    // 网关层 token 校验失败（HTTP 401 空响应体）：清登录态跳登录页
    if (error.response?.status === 401) {
      return handleAuthorized()
    }
    console.log('err' + error) // for debug
    let { message } = error
    const { t } = useI18n()
    if (message === 'Network Error') {
      message = t('sys.api.errorMessage')
    } else if (message.includes('timeout')) {
      message = t('sys.api.apiTimeoutMessage')
    } else if (message.includes('Request failed with status code')) {
      message = t('sys.api.apiRequestFailed') + message.substr(message.length - 3)
    }
    ElMessage.error(message)
    return Promise.reject(error)
  }
)

/** 是否有弹层（el-dialog）正在展示 */
const hasOpenDialog = () => {
  const overlays = document.querySelectorAll('.el-overlay')
  return Array.from(overlays).some((el) => (el as HTMLElement).offsetParent !== null)
}

/**
 * 弹出重新登录确认框。
 * 弹层打开时延后，避免抢在弹层之上遮住正在展示的内容
 * （如重置密码后仅此一次展示的初始密码，重登会刷新页面导致其丢失）。
 */
const showReloginConfirm = () => {
  const { t } = useI18n()
  const confirmRelogin = () => {
    ElMessageBox.confirm(t('sys.api.timeoutMessage'), t('common.confirmTitle'), {
      modalClass: 'app-session-expired-overlay',
      showCancelButton: false,
      closeOnClickModal: false,
      closeOnPressEscape: false,
      showClose: false,
      confirmButtonText: t('login.relogin'),
      type: 'warning'
    }).then(() => {
      resetRouter() // 重置静态路由表
      deleteUserCache() // 删除用户缓存
      removeToken()
      isRelogin.show = false
      // 干掉token后再走一次路由让它过router.beforeEach的校验
      window.location.reload()
    })
  }
  if (!hasOpenDialog()) {
    confirmRelogin()
    return
  }
  // 轮询等待弹层关闭；页面刷新/跳转后定时器自然失效
  const timer = setInterval(() => {
    if (hasOpenDialog()) return
    clearInterval(timer)
    confirmRelogin()
  }, 300)
}

const handleAuthorized = () => {
  const { t } = useI18n()
  if (!isRelogin.show) {
    // 如果已经到重新登录页面则不进行弹窗提示
    if (window.location.href.includes('login?redirect=')) {
      return
    }
    isRelogin.show = true
    showReloginConfirm()
  }
  return Promise.reject(t('sys.api.timeoutMessage'))
}
export { service }
